import { useMemo, useState } from 'react';
import type { AnySourceConfig, ReportColumn } from '../types';
import { defaultKeys } from '../utils';

export function useColumnSelection(source: AnySourceConfig) {
  const [selectedKeys, setSelectedKeys] = useState<string[]>(defaultKeys(source));
  const [search, setSearch] = useState('');

  const selectedColumns = useMemo(
    () => selectedKeys.map((k) => source.columns.find((c) => c.key === k)).filter(Boolean) as ReportColumn[],
    [selectedKeys, source],
  );

  /**
   * What the table and the CSV actually use: the picked columns, then each picked column's Person B
   * twin on the right (same order), then the `trailing` columns (Missing fields, Filled from).
   * The picker and the "selected columns" list keep using `selectedColumns`, so twins never appear
   * there and cannot be ticked or reordered on their own.
   */
  const exportColumns = useMemo(() => {
    const twins = source.twinColumns ?? [];
    if (twins.length === 0) return selectedColumns;

    const main = selectedColumns.filter((c) => !c.trailing);
    const trailing = selectedColumns.filter((c) => c.trailing);
    const extra = main
      .map((c) => (c.twinKey ? twins.find((t) => t.key === c.twinKey) : undefined))
      .filter(Boolean) as ReportColumn[];
    return [...main, ...extra, ...trailing];
  }, [selectedColumns, source]);

  const groupedAvailable = useMemo(() => {
    const q = search.trim().toLowerCase();
    const groups: Record<string, ReportColumn[]> = {};
    source.columns
      .filter((c) => !q || c.label.toLowerCase().includes(q))
      .forEach((c) => (groups[c.category] ??= []).push(c));
    return groups;
  }, [source, search]);

  const toggleColumn = (key: string) =>
    setSelectedKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const reorderColumns = (next: ReportColumn[]) =>
    setSelectedKeys(next.map((c) => c.key));

  return {
    selectedKeys, selectedColumns, exportColumns, groupedAvailable, search, setSearch,
    toggleColumn, reorderColumns,
    selectAll: () => setSelectedKeys(source.columns.map((c) => c.key)),
    selectNone: () => setSelectedKeys([]),
    selectDefault: () => setSelectedKeys(defaultKeys(source)),
    resetFor: (next: AnySourceConfig) => {
      setSelectedKeys(defaultKeys(next));
      setSearch('');
    },
  };
}
